import { useState } from 'react';
import Icon from './Icon';
import Button from './ui/Button';
import { Input, Select, Textarea } from './ui/Input';

export interface ButtonNode {
  label: string;
  action_kind: 'text' | 'image' | 'video' | 'file' | 'url';
  text: string | null;
  media_asset_id: string | null;
  url: string | null;
  children?: ButtonNode[];
}

interface ButtonBuilderProps {
  buttons: ButtonNode[];
  onChange: (buttons: ButtonNode[]) => void;
  depth?: number;
}

export default function ButtonBuilder({ buttons, onChange, depth = 0 }: ButtonBuilderProps) {
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<'text' | 'url'>('text');
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');

  const addButton = (): void => {
    if (label.trim() === '' || buttons.length >= 3) return;
    const node: ButtonNode = {
      label: label.trim().slice(0, 20),
      action_kind: kind === 'url' ? 'url' : 'text',
      text: kind === 'text' && text.trim() !== '' ? text.trim() : null,
      media_asset_id: null,
      url: kind === 'url' && url.trim() !== '' ? url.trim() : null,
      children: [],
    };
    onChange([...buttons, node]);
    setLabel('');
    setText('');
    setUrl('');
  };

  const removeButton = (index: number): void => {
    onChange(buttons.filter((_, i) => i !== index));
  };

  const updateButton = (index: number, patch: Partial<ButtonNode>): void => {
    const next = buttons.map((b, i) => (i === index ? { ...b, ...patch } : b));
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-slate-700">
          دکمه‌ها {depth > 0 && <span className="text-slate-400">(زیرمجموعه)</span>}
          <span className="text-slate-400"> ({buttons.length}/3)</span>
        </p>
      </div>

      {buttons.map((btn, index) => (
        <div key={index} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-[10px] font-bold text-slate-500">
                {index + 1}
              </span>
              <span className="text-xs font-semibold text-slate-800">{btn.label}</span>
              <span className="rounded bg-white px-1.5 py-0.5 text-[10px] text-slate-500">
                {btn.action_kind}
              </span>
            </div>
            <button
              type="button"
              onClick={() => removeButton(index)}
              className="rounded p-1 text-rose-400 hover:bg-white hover:text-rose-600"
              aria-label="حذف دکمه"
            >
              <Icon name="trash" className="h-3.5 w-3.5" />
            </button>
          </div>
          {btn.action_kind === 'text' && btn.text !== null && (
            <p className="mt-2 rounded bg-white px-2 py-1 text-[11px] text-slate-600">{btn.text}</p>
          )}
          {btn.action_kind === 'url' && btn.url !== null && (
            <p
              className="mt-2 truncate rounded bg-white px-2 py-1 text-[11px] text-brand-600"
              dir="ltr"
            >
              {btn.url}
            </p>
          )}

          {depth < 2 && (
            <div className="mt-3 border-t border-slate-200 pt-3">
              <ButtonBuilder
                buttons={btn.children ?? []}
                onChange={(children) => updateButton(index, { children })}
                depth={depth + 1}
              />
            </div>
          )}
        </div>
      ))}

      {buttons.length < 3 && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-slate-300 p-3">
          <div className="w-36">
            <Input
              label="عنوان دکمه"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              maxLength={20}
              placeholder="عنوان"
            />
          </div>
          <Select
            label="نوع"
            value={kind}
            onChange={(e) => setKind(e.target.value as 'text' | 'url')}
            className="w-28"
          >
            <option value="text">متن</option>
            <option value="url">لینک</option>
          </Select>
          {kind === 'text' ? (
            <div className="min-w-0 flex-1">
              <Textarea
                label="متن ارسالی"
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={2}
              />
            </div>
          ) : (
            <div className="min-w-0 flex-1">
              <Input
                label="آدرس لینک"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                dir="ltr"
                placeholder="https://..."
              />
            </div>
          )}
          <Button size="sm" variant="secondary" onClick={addButton} disabled={label.trim() === ''}>
            افزودن
          </Button>
        </div>
      )}
    </div>
  );
}
